import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useRBAC } from '../contexts/RBACContext';

/**
 * Fetches Leads page data entirely from the database via the
 * get_leads_page RPC.  No client-side filtering or sorting.
 */
export function useLeadsDB({
  searchTerm          = '',
  statusFilter        = '',
  showArchived        = false,
  scoreMin            = '',
  scoreMax            = '',
  campaignFilter      = '',   // from campaign management page
  campaignDropdown    = '',   // from dropdown in Leads page
  advCampaigns        = [],   // advanced multi-campaign filter
  advFilterApplied    = false,
  sortBy              = 'created_at',
  sortOrder           = 'desc',
  page                = 1,
  pageSize            = 10,
}) {
  const { assignedCampaigns, isAdminOrHigher } = useRBAC();

  const [leads, setLeads]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const reqId = useRef(0);

  const fetch = useCallback(async () => {
    // RBAC campaigns
    const campaigns = isAdminOrHigher()
      ? null
      : (assignedCampaigns?.length ? assignedCampaigns : []);

    const thisReq = ++reqId.current;
    setLoading(true);
    setError(null);

    try {
      const { data: result, error: rpcErr } = await supabase.rpc('get_leads_page', {
        p_campaigns:         campaigns,
        p_search:            searchTerm       ? searchTerm.trim()  : null,
        p_status_filter:     statusFilter     ? statusFilter       : null,
        p_show_archived:     showArchived,
        p_score_min:         scoreMin         ? parseInt(scoreMin, 10)  : null,
        p_score_max:         scoreMax         ? parseInt(scoreMax, 10)  : null,
        p_campaign_filter:   (!advFilterApplied && campaignFilter)    ? campaignFilter    : null,
        p_campaign_dropdown: (!advFilterApplied && !campaignFilter && campaignDropdown) ? campaignDropdown : null,
        p_adv_campaigns:     (advFilterApplied && advCampaigns?.length) ? advCampaigns : null,
        p_sort_by:           sortBy           || 'created_at',
        p_sort_order:        sortOrder        || 'desc',
        p_page:              page             || 1,
        p_page_size:         pageSize         || 10,
      });

      if (thisReq !== reqId.current) return;
      if (rpcErr) throw rpcErr;

      setLeads(result?.leads      ?? []);
      setTotal(result?.total      ?? 0);
      setTotalPages(result?.total_pages ?? 0);
    } catch (err) {
      if (thisReq === reqId.current) setError(err.message);
    } finally {
      if (thisReq === reqId.current) setLoading(false);
    }
  }, [
    assignedCampaigns,
    isAdminOrHigher,
    searchTerm, statusFilter, showArchived,
    scoreMin, scoreMax,
    campaignFilter, campaignDropdown,
    advCampaigns, advFilterApplied,
    sortBy, sortOrder,
    page, pageSize,
  ]);

  useEffect(() => { fetch(); }, [fetch]);

  // Real-time: re-fetch on any leads or lead_events change
  useEffect(() => {
    const channel = supabase
      .channel('leads-page-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' },       () => fetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lead_events' }, () => fetch())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { leads, total, totalPages, loading, error, refetch: fetch };
}
